/*
  카메라 상태 공유 스토어
  ------------------------------------------------------------------
  /api/my-cameras/status 를 네 곳이 각자 따로 5초마다 부르고 있었다.
    · App.js               (onlineCount 만 필요, 가시성 백오프 없음)
    · LivePreview          (statusMap)
    · PriorityPreviewCard  (statusMap)
    · DevicesPage          (statuses 배열)

  카메라 300대 · 탭 3개면 5초마다 전수 조회가 12번 돈다. 대수에 비례해 늘어난다.
  그래서 폴링을 이 스토어 한 곳으로 모으고, 화면들은 결과를 구독만 한다.
  요청 수가 소비처 수와 무관하게 <탭당 1회>가 된다.

  React 컨텍스트가 아니라 모듈 스토어로 만든 이유:
  App.js 자신도 소비자라서, 컨텍스트로 하면 자기가 제공한 값을 자기가
  받지 못해 트리를 재배치해야 한다. 스토어는 App.js와 하위 컴포넌트가
  똑같은 방식으로 구독할 수 있다.
*/

import {
  DASHBOARD_STATUS_HIDDEN_POLL_MS,
  DASHBOARD_STATUS_POLL_MS,
  buildDashboardStatusMap,
} from "./dashboardUtils";

const EMPTY_STATUSES = [];

// useSyncExternalStore 는 getSnapshot 이 매번 같은 참조를 돌려줘야 한다.
// (새 객체를 만들면 무한 렌더가 난다)
let snapshot = {
  statuses: EMPTY_STATUSES,
  statusMap: {},
  onlineCount: 0,
  loadedOnce: false,
  lastUpdated: null,
};

const subscribers = new Set();

let timer = null;
let inFlight = null;
let visibilityBound = false;

function emit() {
  for (const fn of subscribers) {
    try {
      fn();
    } catch (err) {
      console.error("cameraStatusStore 구독자 오류:", err);
    }
  }
}

function getNextDelay() {
  if (typeof document !== "undefined" && document.hidden) {
    return DASHBOARD_STATUS_HIDDEN_POLL_MS;
  }

  return DASHBOARD_STATUS_POLL_MS;
}

function applyStatuses(statuses) {
  const list = Array.isArray(statuses) ? statuses : EMPTY_STATUSES;

  const online = list.filter(
    (item) => String(item?.liveStatus || "").toUpperCase() === "ONLINE"
  ).length;

  snapshot = {
    statuses: list,
    statusMap: buildDashboardStatusMap(list),
    onlineCount: online,
    loadedOnce: true,
    lastUpdated: new Date(),
  };

  emit();
}

/**
 * 지금 즉시 한 번 조회한다. 이미 조회 중이면 그 결과를 함께 기다린다.
 * 반환값: statuses 배열 (기존 DevicesPage 처럼 반환값을 쓰는 곳 호환용)
 */
export function refreshCameraStatuses() {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch("/api/my-cameras/status", {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data?.success && Array.isArray(data?.statuses)) {
        applyStatuses(data.statuses);
        return data.statuses;
      }

      // 401(로그아웃) 등은 조용히 넘긴다 — 기존 동작과 동일하게 화면을 비우지 않는다.
      return snapshot.statuses;
    } catch (err) {
      console.error("cameraStatusStore 조회 실패:", err);
      return snapshot.statuses;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

function scheduleNext() {
  if (timer) clearTimeout(timer);
  if (subscribers.size === 0) return;

  timer = setTimeout(async () => {
    await refreshCameraStatuses();
    scheduleNext();
  }, getNextDelay());
}

function handleVisibilityChange() {
  if (subscribers.size === 0) return;

  // 탭으로 돌아오면 다음 주기를 기다리지 않고 곧 갱신한다.
  if (timer) clearTimeout(timer);

  timer = setTimeout(async () => {
    await refreshCameraStatuses();
    scheduleNext();
  }, 500);
}

function startPolling() {
  if (typeof document !== "undefined" && !visibilityBound) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    visibilityBound = true;
  }

  refreshCameraStatuses().then(scheduleNext);
}

function stopPolling() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  if (typeof document !== "undefined" && visibilityBound) {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    visibilityBound = false;
  }
}

/** useSyncExternalStore 용 구독 함수. 첫 구독자에서 폴링을 켜고, 마지막에서 끈다. */
export function subscribeCameraStatus(onChange) {
  subscribers.add(onChange);

  if (subscribers.size === 1) {
    startPolling();
  }

  return () => {
    subscribers.delete(onChange);

    if (subscribers.size === 0) {
      stopPolling();
    }
  };
}

export function getCameraStatusSnapshot() {
  return snapshot;
}
